<template>
  <div class="cruise-list">
    <h2>Browse Cruises</h2>
    
    <!-- Add the new advanced filter component -->
    <CruiseFilter 
      ref="cruiseFilter"
      :cruiseLines="cruiseLines"
      :destinations="destinations"
      :departurePorts="departurePorts"
      @filter-applied="handleAdvancedFilters"
    />
    
    <div class="cruise-stats" v-if="listIsCurrent">
      <!-- CP-127: never a bare "Showing 0 of 0"; the empty state below owns 0 results. -->
      <p v-if="shownCount > 0">{{ resultsSummary }}</p>
      <!-- MSC coverage: directly under the count line, above the first card. -->
      <CoverageStrip v-if="shownCount > 0 && mscCoverageText" :text="mscCoverageText" />
      <!-- CP-355: Oceanview matches stored labels only; say what it leaves out. -->
      <CoverageStrip
        v-if="shownCount > 0 && cabinCoverageText"
        :text="cabinCoverageText"
        testid="cabin-coverage-strip"
      />
      <!-- CP-119: one section-level cue when every priced card shares it -->
      <p v-if="sharedPriceCue" class="price-freshness cruise-stats-freshness">{{ sharedPriceCue }}</p>
    </div>
    
    <div class="cruises cruises--priced" v-if="listIsCurrent && shoppableCruises.length">
      <div v-for="cruise in shoppableCruises" :key="cruise.id" class="cruise-card cruise-card--priced">
        <CardIllustrationStrip
          :destination="cruise.destination"
          :destination-id="cruise.destination_id"
          :name="cruise.name"
          tall
        />

        <div class="cruise-body">
          <h3 class="cs-title-case cruise-title cruise-title--clamp" :title="titleCase(cruise.name)">{{ titleCase(cruise.name) }}</h3>
          <p class="cruise-subline">
            <span v-if="cruise.ship_name" class="ship-name">{{ cruise.ship_name }}</span>
            <span v-if="cruise.ship_name && cruise.cruise_line" class="subline-sep" aria-hidden="true">·</span>
            <span v-if="cruise.cruise_line" class="cs-line-pill">{{ cruise.cruise_line }}</span>
          </p>
          <p class="cruise-meta">
            <template v-if="cruise.departure_port">From {{ titleCase(cruise.departure_port) }}</template>
            <template v-if="cruise.departure_port && cruise.duration"> · </template>
            <!-- CP-278: package sailings say 'N-night cruise[, part of an M-night package]' (packageNights.js). -->
            <template v-if="cruise.duration">{{ sailingNightsLabel(cruise) }}</template>
            <template v-if="(cruise.departure_port || cruise.duration)"> · </template>
            {{ formatDate(cruise.departure_date) }}
          </p>
          <!-- CP-217: Flora package label (server-derived; absent for every other row). -->
          <p v-if="cruise.package_label" class="cruise-package">{{ cruise.package_label }}</p>
        </div>

        <div class="cruise-price" v-if="hasBrowsePrice(cruise)">
          <span class="price-label">From</span>
          <span class="price-value">${{ formatBrowsePrice(cruise.current_price) }}</span>
          <span class="price-person">per person</span>
          <span v-if="browseCabinLabel(cruise)" class="cabin-type">({{ browseCabinLabel(cruise) }})</span>
          <span v-if="priceCue(cruise)" class="price-freshness">{{ priceCue(cruise) }}</span>
        </div>
        <div class="cruise-price cruise-price--empty" v-else>
          <span class="price-unavailable">{{ hiddenPriceReason(cruise) }}</span>
          <span v-if="staleAgeStamp(cruise)" class="price-freshness">{{ staleAgeStamp(cruise) }}</span>
        </div>

        <div class="cruise-actions">
          <router-link :to="detailRoute(cruise)" class="view-history-button cs-btn-primary" :aria-label="sailingLinkAriaLabel(cruise)">{{ sailingLinkText(cruise) }}</router-link>
        </div>
      </div>
    </div>

    <section
      v-if="listIsCurrent && unpublishedCruises.length"
      class="unpublished-shells"
      aria-labelledby="unpublished-shells-heading"
    >
      <h3 id="unpublished-shells-heading">Schedule not yet published</h3>
      <p class="unpublished-note">
        These itineraries are in the catalog, but the cruise line has not published a departure date or ship. They are not available to book.
      </p>
      <div class="cruises">
        <article
          v-for="cruise in unpublishedCruises"
          :key="cruise.id"
          class="cruise-card cruise-card--unpublished"
        >
          <div class="cruise-body">
            <h3 class="cs-title-case cruise-title">{{ titleCase(cruise.name) }}</h3>
            <p class="cruise-subline">
              <span v-if="knownShip(cruise)" class="ship-name">{{ cruise.ship_name }}</span>
              <span v-if="knownShip(cruise) && cruise.cruise_line" class="subline-sep" aria-hidden="true">·</span>
              <span v-if="cruise.cruise_line" class="cs-line-pill">{{ cruise.cruise_line }}</span>
            </p>
            <p v-if="nightsLabel(cruise.duration) || realPort(cruise)" class="cruise-meta">
              <template v-if="realPort(cruise)">From {{ realPort(cruise) }}</template>
              <template v-if="realPort(cruise) && nightsLabel(cruise.duration)"> · </template>
              <template v-if="nightsLabel(cruise.duration)">{{ sailingNightsLabel(cruise) }}</template>
            </p>
            <p class="unpublished-label">Schedule not yet published</p>
          </div>
          <div class="cruise-actions">
            <button type="button" @click="viewCruiseDetails(cruise)" class="view-history-button cs-btn-outline" :aria-label="`View listing: ${titleCase(cruise.name)}`">View listing</button>
          </div>
        </article>
      </div>
    </section>
    
    <div v-if="loading" class="loading-indicator app-loading" :class="{ 'loading-indicator--first': firstLoad }">
      <div class="spinner app-spinner"></div>
      <p>Loading cruises...</p>
    </div>

    <div v-else-if="error" class="app-error-state">
      <p>{{ error }}</p>
      <button type="button" class="app-retry-btn" @click="fetchCruises">Try Again</button>
    </div>
    
    <!-- CP-127: filtered to 0 results: recovery (Clear all + remove one filter). -->
    <FilterEmptyRecovery
      v-else-if="filteredEmpty"
      type="cruises"
      :options="relaxOptions"
      @clear-all="clearAllFilters"
      @relax="relaxFilter"
    />

    <EmptyState
      v-else-if="!loading && shoppableCruises.length === 0 && unpublishedCruises.length === 0"
      type="cruises"
      message="No cruises found matching your criteria. Try adjusting your filters."
    />
    
    <div class="pagination" v-if="listIsCurrent && totalPages > 1">
      <button 
        type="button"
        @click="changePage(currentPage - 1)" 
        :disabled="currentPage === 1 || !hasPrevPage"
        class="pagination-button"
        aria-label="Previous page"
      >
        <span class="pagination-label-full">Previous</span>
        <span class="pagination-label-short" aria-hidden="true">Prev</span>
      </button>
      
      <div class="page-numbers">
        <button 
          type="button"
          v-for="page in displayedPageNumbers" 
          :key="page" 
          @click="changePage(page)"
          :class="['page-number', { active: currentPage === page }]"
          :disabled="page === '...'"
          :aria-current="currentPage === page ? 'page' : undefined"
        >
          {{ page }}
        </button>
      </div>
      
      <button 
        type="button"
        @click="changePage(currentPage + 1)" 
        :disabled="currentPage === totalPages || !hasNextPage"
        class="pagination-button"
        aria-label="Next page"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
import { formatSailDate } from '@/utils/formatSailDate'
import {
  hiddenPriceReason,
  listingCabinLabel,
  listingPriceCue,
  sharedListingPriceCue,
  showsListingPrice,
  staleAgeStamp
} from '@/utils/priceFreshness'
import { isShipUnknown, isUnpublishedShell } from '@/utils/unpublishedShell'
import { mscCoverageNote } from '@/utils/mscCoverage'
import { cabinCoverageNote, cabinSummaryTail } from '@/utils/cabinCoverage'
import CoverageStrip from '@/components/CoverageStrip.vue'

import apiService from '@/services/api';
import CruiseFilter from '@/components/CruiseFilter.vue';
import { browseListIsCurrent, filterSignature } from '@/utils/browseResultGate';
import { apiFiltersFromQuery, browseStateFromQuery } from '@/utils/browseQuery';
import { destinationsFromCruises } from '@/utils/canonicalDestinations';
import EmptyState from '@/components/EmptyState.vue';
import FilterEmptyRecovery from '@/components/FilterEmptyRecovery.vue';
import { browseRelaxOptions } from '@/utils/emptyRecovery';
import { labelForDestinationId, normalizeCanonicalDestinations } from '@/utils/canonicalDestinations';
import { labelForDeparturePort, normalizeDeparturePortOptions } from '@/utils/canonicalDeparturePorts';

import CardIllustrationStrip from '@/components/CardIllustrationStrip.vue'
import { sailingLinkAriaLabel, sailingLinkText } from '@/utils/sailingLinkLabel'

import { formatPriceAmount } from '@/utils/formatMoney'
import { formatPlaceTitle } from '@/utils/formatPlaceTitle'
import { nightsLabel, sailingNightsLabel } from '@/utils/packageNights'
export default {
  components: {
    CruiseFilter,
    EmptyState,
    FilterEmptyRecovery,
    CardIllustrationStrip,
    CoverageStrip
  },
  data() {
    return {
      cruises: [],
      cruiseLines: [],
      destinations: [],
      departurePorts: [],
      selectedCruiseLine: '',
      selectedDestination: '',
      selectedDeparturePort: '',
      selectedDuration: '',
      loading: false,
      error: null,
      currentPage: 1,
      pageSize: 20,
      totalCruises: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
      advancedFilters: {},
      // Global cruise-line count from /api/filters/all (all 10 lines), distinct
      // from the page-local `cruiseLines` set built from the current page's 20
      // cruises. Used for the "from N cruise lines" summary (CP-41).
      totalCruiseLines: 0,
      // CP-127: global destination / port options from the same /filters/all
      // call, so recovery actions print labels ("Caribbean"), not slugs.
      labelDestinations: [],
      labelDeparturePorts: [],
      // Monotonic id so a slow unfiltered (or prior) response cannot overwrite
      // a newer filtered fetch — root cause of mixed lines after selecting Princess.
      fetchSeq: 0,
      // Signature of the filters that produced `cruises`. Null until the first
      // successful response. A new line filter changes advancedFilters before
      // the fetch returns; until the signatures match, the previous page is hidden.
      resultsFilterKey: null
    };
  },
  computed: {
    /** CP-230: initial load = loading with no list in memory (see .loading-indicator--first). */
    firstLoad() {
      return this.loading && this.cruises.length === 0 && !this.error;
    },
    listIsCurrent() {
      return browseListIsCurrent(this.advancedFilters, this.resultsFilterKey);
    },
    /** MSC coverage copy for the active line filter ('' = no strip). */
    mscCoverageText() {
      return mscCoverageNote(this.advancedFilters && this.advancedFilters.cruise_line);
    },
    /** CP-355: coverage copy for the active cabin filter ('' = no strip). */
    cabinCoverageText() {
      return cabinCoverageNote(this.advancedFilters && this.advancedFilters.cabin);
    },
    shoppableCruises() {
      return this.cruises.filter((cruise) => !isUnpublishedShell(cruise));
    },
    unpublishedCruises() {
      return this.cruises.filter((cruise) => isUnpublishedShell(cruise));
    },
    shownCount() {
      return this.shoppableCruises.length + this.unpublishedCruises.length;
    },
    /**
     * CP-127: one "Remove ..." action per active filter, most restrictive
     * first. Read from the URL (CP-128 source of truth) so labels match the
     * chips; sort is not a filter.
     */
    relaxOptions() {
      const state = browseStateFromQuery(this.$route.query || {});
      const destinations = this.labelDestinations.length ? this.labelDestinations : this.destinations;
      const ports = this.labelDeparturePorts;
      return browseRelaxOptions(state, {
        destination: (id) => labelForDestinationId(destinations, id),
        departure_port: (key) => labelForDeparturePort(ports, key)
      });
    },
    /** CP-127: the current result set is empty because of active filters. */
    filteredEmpty() {
      return this.listIsCurrent && this.shownCount === 0 && this.relaxOptions.length > 0;
    },
    /** CP-119: shown once under the results summary instead of on every card. */
    sharedPriceCue() {
      return sharedListingPriceCue(this.shoppableCruises);
    },
    /** CP-32: do not claim "from N cruise lines" when a single line filter is active */
    resultsSummary() {
      const shown = this.shoppableCruises.length + this.unpublishedCruises.length;
      const total = this.totalCruises;
      const line = (this.advancedFilters && this.advancedFilters.cruise_line) || '';
      // CP-355: '' with no cabin filter, so the no-cabin summary is unchanged.
      const cabinTail = cabinSummaryTail(this.advancedFilters && this.advancedFilters.cabin);
      if (this.shoppableCruises.length === 0 && this.unpublishedCruises.length > 0) {
        const lineSuffix = line ? ` · ${line}` : '';
        return `Showing ${shown} of ${total} itineraries · Schedule not yet published${lineSuffix}${cabinTail}`;
      }
      if (line) {
        return `Showing ${shown} of ${total} cruises · ${line}${cabinTail}`;
      }
      const n = this.totalCruiseLines || this.cruiseLines.length;
      if (n > 0) {
        return `Showing ${shown} of ${total} cruises from ${n} cruise line${n === 1 ? '' : 's'}${cabinTail}`;
      }
      return `Showing ${shown} of ${total} cruises${cabinTail}`;
    },
    displayedPageNumbers() {
      const pages = [];
      const maxVisiblePages = 5;
      
      if (this.totalPages <= maxVisiblePages) {
        // Show all pages if there are fewer than maxVisiblePages
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);
        
        // Calculate start and end of page range around current page
        let start = Math.max(2, this.currentPage - 1);
        let end = Math.min(this.totalPages - 1, this.currentPage + 1);
        
        // Adjust if we're near the beginning
        if (this.currentPage <= 3) {
          end = Math.min(this.totalPages - 1, 4);
        }
        
        // Adjust if we're near the end
        if (this.currentPage >= this.totalPages - 2) {
          start = Math.max(2, this.totalPages - 3);
        }
        
        // Add ellipsis if needed
        if (start > 2) {
          pages.push('...');
        }
        
        // Add page numbers
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        // Add ellipsis if needed
        if (end < this.totalPages - 1) {
          pages.push('...');
        }
        
        // Always show last page
        pages.push(this.totalPages);
      }
      
      return pages;
    }
  },
  mounted() {
    this.applyQueryParams();
    this.fetchCruises();
    this.fetchTotalCruiseLines();
  },
  watch: {
    '$route.query': {
      handler() {
        // CP-128: the query string is the source of truth for Browse filters
        // and sort. Back/forward or an in-app link re-reads it and re-fetches.
        // CruiseFilter's own URL writes already emitted the same filters, so
        // they are skipped here instead of fetching twice.
        if (this.$route.name !== 'CruiseList') return;
        if (!this.applyQueryParams()) return;
        this.currentPage = 1;
        this.fetchCruises();
      }
    }
  },
  methods: {
    // CP-212: one pattern for every sailing link (visible text + ship/date accessible name).
    sailingLinkText,
    sailingLinkAriaLabel,
    detailRoute(cruise) {
      const query = {};
      if (cruise && cruise.departure_date) query.sail_date = cruise.departure_date;
      return { path: `/cruise/${cruise.id}`, query };
    },
    async fetchTotalCruiseLines() {
      // One-time fetch of the global filter metadata to get the true cruise-line
      // count for the summary (the page-local set only sees the current page).
      try {
        const response = await apiService.getFilterOptions();
        const lines = response.data && response.data.cruise_lines;
        if (Array.isArray(lines)) {
          this.totalCruiseLines = lines.length;
        }
        const data = response.data || {};
        this.labelDestinations = normalizeCanonicalDestinations(data.destinations);
        this.labelDeparturePorts = normalizeDeparturePortOptions(data.departure_ports || []);
      } catch (error) {
        console.error('Error fetching global filter options:', error);
        // Leave totalCruiseLines at 0 so resultsSummary falls back to the
        // page-local cruiseLines count.
      }
    },
    /**
     * CP-128 (+ CP-123): decode every Browse param (destination, month,
     * start_date/end_date, cruise_line, duration, price, sort, …) so a Home
     * search or shared deep link is filtered on first paint. Returns true
     * when the active filters changed.
     */
    applyQueryParams() {
      const next = apiFiltersFromQuery(this.$route.query || {});
      if (filterSignature(next) === filterSignature(this.advancedFilters)) return false;
      this.advancedFilters = next;
      return true;
    },
    async fetchCruises() {
      const seq = ++this.fetchSeq;
      this.loading = true;
      // Keep previous cruises/totals in memory until replacement data arrives.
      // Clearing at fetch start caused a flash-then-empty when a slow/timed-out
      // follow-up overwrote a successful load (e.g. flash 20/7646 then empty).
      // The template hides that page while resultsFilterKey does not match the
      // filters in effect, so a line apply cannot flash NCL/MSC cards under a
      // Holland America header (CP-103). The loader shows until the match.
      try {
        // Build query parameters for pagination and filters
        const params = {
          page: this.currentPage,
          per_page: this.pageSize,
          ...this.advancedFilters
        };
        
        const response = await apiService.getCruises(params);
        // Discard stale responses (e.g. initial unfiltered fetch finishing after
        // a cruise_line-filtered Apply — previously left Browse showing mixed lines).
        if (seq !== this.fetchSeq) return;
        this.error = null;
        
        // Only replace list and pagination after a successful response
        this.cruises = response.data.cruises || [];
        
        // Update pagination data
        const pagination = response.data.pagination || {};
        this.totalCruises = pagination.total || 0;
        this.totalPages = pagination.pages || 1;
        this.hasNextPage = pagination.has_next || false;
        this.hasPrevPage = pagination.has_prev || false;
        
        // If we're on a page that no longer exists after filtering, go to page 1
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
          this.currentPage = 1;
          return this.fetchCruises();
        }
        
        // Extract filter options if this is the first load
        if (!this.cruiseLines.length) {
          // Extract from the data
          this.cruiseLines = [...new Set(this.cruises.map(c => c.cruise_line).filter(Boolean))].sort();
          this.destinations = destinationsFromCruises(this.cruises);
          this.departurePorts = [...new Set(this.cruises.map(c => c.departure_port).filter(Boolean))].sort();
        }
        
        this.resultsFilterKey = filterSignature(this.advancedFilters);
        this.error = null;
        this.loading = false;
      } catch (error) {
        if (seq !== this.fetchSeq) return;
        console.error('Error fetching cruises:', error);
        this.error = 'Failed to load cruise data. Please try again later.';
        // Do not clear this.cruises / totals — keep previous successful data
        this.loading = false;
      }
    },
    handleAdvancedFilters(filters) {
      // Store the advanced filters
      this.advancedFilters = filters;
      
      // Reset to page 1 when applying new filters
      this.currentPage = 1;
      
      // Fetch cruises with the new filters
      this.fetchCruises();
    },
    /**
     * CP-127: recovery actions go through CruiseFilter so its chips/panel,
     * the URL (CP-128), and this list stay in step. No page reload.
     */
    clearAllFilters() {
      const filter = this.$refs.cruiseFilter;
      if (filter && typeof filter.clearAllFilters === 'function') filter.clearAllFilters();
    },
    relaxFilter(option) {
      const filter = this.$refs.cruiseFilter;
      if (!option || !option.patch) return;
      if (filter && typeof filter.relaxFilter === 'function') filter.relaxFilter(option.patch);
    },
    applyCruiseFilters() {
      // Reset to page 1 when applying new filters
      this.currentPage = 1;
      this.fetchCruises();
    },
    changePage(page) {
      if (page === '...') return;
      this.currentPage = page;
      this.fetchCruises();
    },
    viewCruiseDetails(cruise) {
      this.$router.push(this.detailRoute(cruise));
    },
    getCruiseLineClass(cruiseLine) {
      // Generate a class based on cruise line name for styling
      return 'cruise-line-' + cruiseLine.toLowerCase().replace(/\s+/g, '-');
    },
    formatDate(dateString) {
      return formatSailDate(dateString, { year: 'numeric', month: 'long', day: 'numeric' });
    },
    titleCase(str) {
      return formatPlaceTitle(str);
    },
    listingPriceCue,
    priceCue(cruise) {
      // CP-119: the section line already carries this exact label. A card
      // whose label differs keeps its own cue beside the fare.
      const cue = listingPriceCue(cruise);
      return cue && cue === this.sharedPriceCue ? '' : cue;
    },
    staleAgeStamp,
    hiddenPriceReason,
    hasBrowsePrice(cruise) {
      return showsListingPrice(cruise);
    },
    browseCabinLabel(cruise) {
      // CP-79: only label a shown fare; never invent a cabin on unavailable.
      return listingCabinLabel(cruise);
    },
    formatBrowsePrice(price) {
      return formatPriceAmount(price);
    },
    nightsLabel(n) {
      return nightsLabel(n);
    },
    sailingNightsLabel(cruise) {
      return sailingNightsLabel(cruise);
    },
    knownShip(cruise) {
      return !isShipUnknown(cruise);
    },
    realPort(cruise) {
      const port = String((cruise && cruise.departure_port) || '').trim();
      if (!port || /^(unknown|n\/a|na|none|null|tbd|missing port content)$/i.test(port)) return '';
      return formatPlaceTitle(port);
    },
  }
};
</script>

<style scoped>
.cruise-list {
  margin: 0;
  padding: 0;
  max-width: 100%;
  box-sizing: border-box;
}

h2 {
  text-align: center;
  margin-bottom: var(--space-5);
  color: var(--color-text);
  font-family: var(--font-heading);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin-bottom: var(--space-5);
  padding: var(--space-4);
  background-color: var(--color-surface);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

.filter-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 200px;
}

.filter-group label {
  margin-bottom: var(--space-1);
  font-weight: 600;
  color: var(--color-text-muted);
}

.filter-group select {
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--text-body);
  background: var(--color-surface);
  color: var(--color-text);
}

.filter-group select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

.filter-button {
  align-self: flex-end;
  padding: var(--space-3) var(--space-5);
  background-color: var(--cs-fill);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  transition: background var(--transition-normal);
}

.filter-button:hover {
  background-color: var(--cs-fill-hover);
}

.cruise-stats {
  margin-bottom: var(--space-5);
  color: var(--color-text-muted);
  font-size: var(--text-small);
}

.cruise-stats p {
  margin: 0;
}

.cruise-stats .cruise-stats-freshness {
  margin-top: var(--space-1);
  text-align: left;
}

.cruises {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 768px) {
  .cruises {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-5);
  }
}

@media (min-width: 1024px) {
  .cruises {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

.cruise-card {
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--cs-card);
  box-shadow: var(--shadow-card);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
  display: flex;
  flex-direction: column;
}

.cruise-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-card-hover);
}

/* CP-119: priced cards share four row tracks (strip / body / price /
 * actions) with their row neighbours via subgrid, so the green price block
 * and the CTA line up across a row and every card in a row is the same
 * height, whatever the title or port length. */
.cruise-card--priced {
  display: grid;
  grid-row: span 4;
  grid-template-rows: subgrid;
  row-gap: 0;
}

.cruise-title--clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.unpublished-shells {
  margin-top: var(--space-6);
  padding-top: var(--space-5);
  border-top: 1px solid var(--color-border);
}

.unpublished-shells h3 {
  margin: 0 0 var(--space-2);
  text-align: left;
  font-family: var(--font-heading);
  font-size: 20px;
  color: var(--color-text);
}

.unpublished-note,
.unpublished-label {
  margin: 0;
  color: var(--color-text-muted);
  line-height: 1.4;
}

.unpublished-note {
  margin-bottom: var(--space-4);
  max-width: 42rem;
}

.unpublished-label {
  font-weight: 600;
  font-style: italic;
}

.cruise-card--unpublished {
  background: var(--color-background);
  box-shadow: none;
}

.cruise-card--unpublished:hover {
  transform: none;
  box-shadow: none;
}


@media (min-width: 768px) {
}

.cruise-body {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1;
}

.cruise-title {
  margin: 0;
  font-family: var(--font-heading);
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  color: var(--cs-ink);
  text-transform: none;
}

.cruise-subline {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  font-size: 13px;
  line-height: 18px;
  color: var(--cs-muted);
}

.ship-name {
  font-weight: 500;
  color: var(--cs-ink);
}

.subline-sep {
  opacity: 0.55;
}

.cruise-meta {
  margin: var(--space-1) 0 0;
  font-size: var(--text-small);
  line-height: 1.45;
  color: var(--cs-muted);
}

.cruise-package {
  margin: var(--space-1) 0 0;
  font-size: var(--text-small);
  line-height: 1.45;
  color: var(--cs-ink);
  font-weight: 500;
}

.cruise-price {
  padding: var(--space-4);
  background-color: var(--color-accent-success-bg);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cruise-price--empty {
  background-color: var(--cs-sand);
  justify-content: center;
}

.price-label {
  font-size: var(--text-small);
  color: var(--color-text-muted);
}

.price-value {
  font-size: var(--text-h2);
  font-weight: bold;
  color: var(--cs-price);
}

.price-person {
  font-size: var(--text-caption);
  color: var(--color-text-muted);
}

.cabin-type {
  font-size: var(--text-caption);
  color: var(--cs-muted);
  font-weight: normal;
  font-style: italic;
}

.price-freshness {
  margin-top: var(--space-2);
  font-size: var(--text-caption);
  line-height: 1.35;
  color: var(--color-text-muted);
  text-align: center;
}

.price-unavailable {
  color: var(--color-text-muted);
  font-style: italic;
}


.cruise-actions {
  display: flex;
  padding: var(--space-4);
  gap: var(--space-3);
  margin-top: auto;
}

.view-history-button, .view-details-button {
  flex: 1;
  width: 100%;
  min-height: var(--touch-min);
  padding: var(--space-3) var(--space-4);
  text-align: center;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
  transition: background var(--transition-normal), transform var(--transition-fast);
}

.view-history-button {
  background-color: var(--cs-fill);
  color: white;
  border: none;
}

.view-history-button:hover:not(:disabled) {
  background-color: var(--cs-fill-hover);
}

.view-history-button:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.view-details-button {
  background-color: var(--color-secondary);
  color: white;
  border: none;
  display: inline-block;
}

.view-details-button:hover {
  background-color: var(--color-secondary-hover);
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: var(--space-6) 0;
}

/* CP-230: desktop Browse CLS. While the list loads the page was only ~one screen tall, so
   footer.main-footer sat inside the first viewport and then jumped thousands of px down when the
   cards rendered (CLS 0.17-0.23 at 1280). The loader is now taller than the viewport, so the footer
   starts (and stays) below the fold until the list arrives, same trick as CP-211 on Detail.
   Spinner stays at the top, where it was. The box exists only while loading; a short or empty
   result takes its natural height afterwards, so there is no standing gap. First load only
   (no list in memory yet): a refetch (page / sort / filter) keeps the old short loader, because a
   100vh box there makes the page jump (measured 0.57-0.80 on "Next page"). */
.cruise-list > .loading-indicator--first {
  min-height: 100vh;
  min-height: 100svh;
  justify-content: flex-start;
}

.loading-indicator p {
  color: var(--color-text-muted);
  margin-top: var(--space-4);
}

.spinner {
  border: 4px solid var(--color-border-light);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border-top-color: var(--color-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: var(--space-5);
  padding: var(--space-3) 0;
  gap: var(--space-2);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.pagination-button {
  min-height: var(--touch-min);
  padding: var(--space-2) var(--space-3);
  background-color: var(--cs-fill);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  transition: background var(--transition-normal);
  flex: 0 1 auto;
  box-sizing: border-box;
}

.pagination-label-short {
  display: none;
}

.pagination-button:hover:not(:disabled) {
  background-color: var(--cs-fill-hover);
}

.pagination-button:disabled {
  background-color: var(--color-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.page-numbers {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: 0;
  gap: var(--space-1);
  max-width: 100%;
  box-sizing: border-box;
}

.page-number {
  min-width: var(--touch-min);
  min-height: var(--touch-min);
  height: auto;
  padding: 0 var(--space-2);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background-color: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-sizing: border-box;
  flex: 0 0 auto;
}

.page-number:hover:not(.active):not(:disabled) {
  background-color: var(--color-background);
}

.page-number:disabled {
  cursor: default;
  opacity: 0.7;
}

.page-number.active {
  background-color: var(--cs-fill);
  color: white;
  border-color: var(--color-primary);
}

/* Phone widths (~360–390px): shorten Prev label and tighten so pager fits viewport */
@media (max-width: 480px) {
  .pagination {
    gap: var(--space-1);
    padding: var(--space-2) 0;
  }

  .pagination-label-full {
    display: none;
  }

  .pagination-label-short {
    display: inline;
  }

  .pagination-button {
    padding: var(--space-2) var(--space-2);
  }

  .page-number {
    min-width: 40px;
    padding: 0 var(--space-1);
  }
}
</style>
